import { MenuItemVariantDto, MenuSectionDto } from '../../generated-api';

// Cache regex patterns to avoid recreating them on every call
const BARCODE_REGEX = /^\d{13}$/;
const NUMERIC_REGEX = /^\d+$/;
const LEADING_ZEROS_REGEX = /^0+/;

/**
 * Check if search text looks like a barcode (13 digits)
 * This includes both embedded barcodes (starting with '2') and standard GTIN-13
 */
function isBarcode(searchText: string): boolean {
  // Fast path: check length first before regex
  const cleaned = searchText.trim();
  return cleaned.length === 13 && BARCODE_REGEX.test(cleaned);
}

/**
 * Normalize a code by removing leading zeros (cached regex)
 */
function normalizeCode(code: string): string {
  return code.replace(LEADING_ZEROS_REGEX, '') || '0';
}

export const filterItemsOnSearch = (sections: MenuSectionDto[], searchText: string): MenuSectionDto[] => {
  if (!searchText) {
    return sections;
  }

  const trimmed = searchText.trim();
  if (!trimmed) {
    return sections;
  }

  // If search text looks like a barcode (13 digits), don't filter items
  // Let barcode parsing handle it instead to avoid false matches
  if (isBarcode(trimmed)) {
    return sections.map((section) => ({
      ...section,
      items: [],
      subsections: section.subsections.map((sub) => ({ ...sub, items: [] })),
    }));
  }

  // Pre-compute search values once
  const searchLower = trimmed.toLowerCase();
  const isNumericSearch = NUMERIC_REGEX.test(searchLower);
  const normalizedSearch = isNumericSearch ? normalizeCode(searchLower) : null;

  const matchesSearch = (item: MenuItemVariantDto): boolean => {
    // PLU matching - only check if we have a PLU
    if (item.plu && item.plu.toLowerCase().startsWith(searchLower)) {
      return true;
    }

    // GTIN matching - only exact match and normalized exact match
    if (item.gtin) {
      const gtinLower = item.gtin.trim().toLowerCase();
      if (gtinLower === searchLower) {
        return true;
      }
      if (normalizedSearch && normalizeCode(gtinLower) === normalizedSearch) {
        return true;
      }
    }

    // For numeric searches, only PLU/GTIN matches
    if (isNumericSearch) {
      return false;
    }

    // Name matching
    if (item.name) {
      return item.name.toLowerCase().includes(searchLower);
    }

    return false;
  };

  return sections.map((section) => ({
    ...section,
    items: section.items.filter(matchesSearch),
    subsections: section.subsections.map((sub) => ({
      ...sub,
      items: sub.items.filter(matchesSearch),
    })),
  }));
};
